import React, { useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Filter,
  ArrowDownCircle,
  ArrowUpCircle,
  TrendingUp,
  Loader2,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import SearchSelect from "components/ui/SearchSelect";
import Modal from "components/ui/Modal";
import Button from "components/ui/Button";
import DatePicker from "components/financial/DatePicker";
import { useGetFinancialsQuery } from "apis/financial";
import {
  useGetPlatformCostSummaryQuery,
  useSetPlatformMonthlyPriceMutation,
} from "apis/financialSummary";
import {
  useGetExpensesQuery,
  useCreateExpenseMutation,
  useUpdateExpenseMutation,
  useDeleteExpenseMutation,
} from "apis/expense";
import {
  useGetExpenseCategoriesQuery,
  useCreateExpenseCategoryMutation,
  useUpdateExpenseCategoryMutation,
  useDeleteExpenseCategoryMutation,
} from "apis/expenseCategory";
import {
  useGetProductTagsQuery,
  useCreateProductTagMutation,
  useUpdateProductTagMutation,
} from "apis/productTag";
import { useGetOrganizationsQuery } from "apis/organization";
import { formatCurrency } from "utils/helperFunctions";
import Table from "components/ui/Table";
import { showToast } from "components/ui/toast";
import AiCostBreakdown from "components/financial/AiCostBreakdown";
import CategoryProductInput from "components/financial/ProductInput";
import ProductSplitEditor, {
  describeSplit,
} from "components/financial/ProductSplitEditor";
import CostBreakdownModal, {
  CostBreakdownSubtree,
  CostBreakdownEmpty,
  CostBreakdownLoading,
} from "components/financial/CostBreakdownModal";
import {
  TwilioCostBreakdown,
  DomainCostBreakdown,
  AyrshareCostBreakdown,
  RegistrySearchBreakdown,
  // XERO-DISABLED: XeroCostBreakdown,
} from "components/financial/ServiceCostBreakdowns";

const PLATFORMS = [
  {
    key: "openai",
    label: "OpenAI",
    dotColor: "bg-green-500",
    textColor: "text-green-700",
    description: "AI writing & analysis",
  },
  {
    key: "gemini",
    label: "Gemini",
    dotColor: "bg-blue-500",
    textColor: "text-blue-700",
    description: "AI search & insights",
  },
  {
    key: "anthropic",
    label: "Anthropic",
    dotColor: "bg-orange-500",
    textColor: "text-orange-700",
    description: "AI conversations",
  },
  {
    key: "twilio",
    label: "Twilio",
    dotColor: "bg-cyan-500",
    textColor: "text-cyan-700",
    description: "SMS messaging",
  },
  {
    key: "intellisms",
    label: "IntelliSMS",
    dotColor: "bg-teal-500",
    textColor: "text-teal-700",
    description: "SMS messaging",
  },
  {
    key: "domain",
    label: "Domain",
    dotColor: "bg-emerald-500",
    textColor: "text-emerald-700",
    description: "Property data API",
  },
  {
    key: "ayrshare",
    label: "Ayrshare",
    dotColor: "bg-violet-500",
    textColor: "text-violet-700",
    description: "Social posting",
  },
  {
    key: "dyedurham",
    label: "Dye & Durham",
    dotColor: "bg-orange-500",
    textColor: "text-orange-700",
    description: "Land title searches",
  },
  {
    key: "globaldata",
    label: "GlobalData",
    dotColor: "bg-cyan-500",
    textColor: "text-cyan-700",
    description: "ASIC company searches",
  },
];

const AI_PROVIDER_KEYS = ["openai", "gemini", "anthropic"];

// Default to current month (1st → last day)
const buildCurrentMonth = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const last = new Date(y, now.getMonth() + 1, 0).getDate();
  return {
    startDate: `${y}-${m}-01`,
    endDate: `${y}-${m}-${String(last).padStart(2, "0")}`,
  };
};

const EXPENSE_TREE_LEVELS = [
  {
    header: "Category",
    columns: [
      { key: "count", header: "Items", align: "right" },
      { key: "total", header: "Total", align: "right" },
    ],
  },
];

const allActiveByDefault = () =>
  PLATFORMS.reduce((acc, p) => ({ ...acc, [p.key]: true }), {});

const FinancialSummary = () => {
  const [activePlatforms, setActivePlatforms] = useState(allActiveByDefault);
  // Which cost row's drill-down modal is open (null = none).
  const [drillRow, setDrillRow] = useState(null);
  const [drillCategory, setDrillCategory] = useState(null); // { label, _items, total }
  const [dateValue, setDateValue] = useState(buildCurrentMonth);

  // Custom expense state
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isExpenseFormOpen, setIsExpenseFormOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    name: "",
    amount: "",
    date: "",
    category: "",
    description: "",
    isSystem: true,
    organizationId: "",
  });

  // Category management state
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryInput, setCategoryInput] = useState("");
  const [editingCategory, setEditingCategory] = useState(null); // { _id, name }
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const [splitCategoryId, setSplitCategoryId] = useState(null); // category whose product split is open

  // Drill-down state lives in the URL (?organization=<orgId>) so back/forward
  // and shared links land on the same organization, without
  // ever leaving this route.
  const [searchParams, setSearchParams] = useSearchParams();
  const organizationId = searchParams.get("organization");

  const togglePlatform = (key) =>
    setActivePlatforms((prev) => ({ ...prev, [key]: !prev[key] }));
  const resetPlatforms = () => setActivePlatforms(allActiveByDefault());

  /*  Shared date params — every API call on this page uses these.              */
  /*  DatePicker outputs { startDate, endDate } in YYYY-MM-DD format.          */
  /*  Empty strings mean "All Time" (no date filter).                          */

  const hasDate = !!dateValue.startDate && !!dateValue.endDate;

  // AI + Twilio endpoints use startDate / endDate
  const dateParams = useMemo(() => {
    if (!hasDate) return {};
    return { startDate: dateValue.startDate, endDate: dateValue.endDate };
  }, [hasDate, dateValue]);

  // financials uses `period` (or startDate/endDate)
  const financialParams = useMemo(() => {
    if (!hasDate) return {};
    return {
      period: "custom",
      startDate: dateValue.startDate,
      endDate: dateValue.endDate,
    };
  }, [hasDate, dateValue]);

  // externalApi / ayrshare / expense use `from` / `to`
  const rangeParams = useMemo(() => {
    if (!hasDate) return {};
    return { from: dateValue.startDate, to: dateValue.endDate };
  }, [hasDate, dateValue]);

  /*  API: Money In  */
  const {
    data: financialData,
    isPending: isFinancialLoading,
    isError: isFinancialError,
  } = useGetFinancialsQuery(financialParams);

  const totals = financialData?.totals || {};
  const breakdown = financialData?.breakdown || {};
  const cards = breakdown.cards || {};

  /*  API: Money Out — single summary endpoint for all platforms.              */
  /*  Returns usage counts + manually-entered monthly costs. Redis-cached.    */
  const {
    data: platformSummary,
    isPending: isCostLoading,
    isError: isCostError,
  } = useGetPlatformCostSummaryQuery(rangeParams);
  const { mutate: setMonthlyPrice } = useSetPlatformMonthlyPriceMutation();

  // Shared date params for drill-down queries (passed to modals).
  const aiParams = dateParams;
  const { data: expenseData, isPending: isExpensesLoading } =
    useGetExpensesQuery(rangeParams);
  const { mutate: createExpense, isPending: isCreating } =
    useCreateExpenseMutation();
  const { mutate: updateExpense, isPending: isUpdating } =
    useUpdateExpenseMutation();
  const { mutate: deleteExpense, isPending: isDeleting } =
    useDeleteExpenseMutation();

  // Expense categories — needed on page for category sort order + expense form.
  const isExpenseUIOpen = isExpenseModalOpen || isExpenseFormOpen;
  const { data: expenseCategories } = useGetExpenseCategoriesQuery();
  const { mutate: createCategory, isPending: isCreatingCategory } =
    useCreateExpenseCategoryMutation();
  const { mutate: updateCategory, isPending: isUpdatingCategory } =
    useUpdateExpenseCategoryMutation();
  const { mutate: deleteCategory, isPending: isDeletingCategory } =
    useDeleteExpenseCategoryMutation();

  const categoryOptions = useMemo(
    () =>
      (expenseCategories || []).map((c) => ({ value: c._id, label: c.name })),
    [expenseCategories],
  );

  // Product tags — only categoryProducts needed on the page itself.
  // Other product tags (ai, sms, domain, ayrshare) are fetched inside
  // their respective drill-down components.
  const { data: categoryProducts } = useGetProductTagsQuery("expenseCategory");
  const { mutate: createProduct, isPending: isCreatingProduct } =
    useCreateProductTagMutation();
  const { mutate: updateProduct, isPending: isUpdatingProduct } =
    useUpdateProductTagMutation();
  const saveProduct = ({ id, keyType, key, product, splits }, options) => {
    if (id) {
      updateProduct({ id, keyType, product, splits }, options);
    } else {
      createProduct({ keyType, key, product, splits }, options);
    }
  };
  const categoryTagFor = useCallback(
    (categoryId) =>
      (categoryProducts ?? []).find(
        (p) => p.keyType === "expenseCategory" && p.key === categoryId,
      ),
    [categoryProducts],
  );

  // Org list for custom expense form — lazy-loaded when form is open.
  const { data: allOrganizations } = useGetOrganizationsQuery(isExpenseUIOpen);
  const orgOptions = useMemo(
    () =>
      (allOrganizations || []).map((o) => ({ value: o._id, label: o.name })),
    [allOrganizations],
  );

  const expenses = expenseData?.expenses ?? [];
  const expenseTotal = expenseData?.total ?? 0;
  const expenseCount = expenseData?.count ?? 0;

  // Group expenses by category → CostBreakdownModal node format
  const categoryNodes = useMemo(() => {
    const map = new Map();
    for (const exp of expenses) {
      // category is now a populated object { _id, name } — fall back to "Uncategorized"
      const cat = exp.category?.name || "Uncategorized";
      if (!map.has(cat)) map.set(cat, { _items: [], total: 0 });
      const bucket = map.get(cat);
      bucket._items.push(exp);
      bucket.total += exp.amount || 0;
    }
    // Sort by backend category order (alphabetical from API), Uncategorized last
    const order = [
      ...(expenseCategories || []).map((c) => c.name),
      "Uncategorized",
    ];
    return [...map.entries()]
      .sort(([a], [b]) => {
        const ai = order.indexOf(a);
        const bi = order.indexOf(b);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      })
      .map(([cat, { _items, total }]) => {
        // Resolve the category's ObjectId from the first expense's populated category
        const catObj = _items[0]?.category;
        const _categoryId = catObj?._id ? String(catObj._id) : null;
        return {
          id: cat,
          label: cat,
          lazy: true,
          total,
          _categoryId,
          values: {
            count: String(_items.length),
            total: formatCurrency(total),
          },
          _items,
        };
      });
  }, [expenses, expenseCategories]);

  // Custom expense form handlers
  const openCreateForm = () => {
    setExpenseForm({
      name: "",
      amount: "",
      date: "",
      category: "",
      description: "",
      isSystem: true,
      organizationId: "",
    });
    setEditingExpense(null);
    setIsExpenseFormOpen(true);
  };
  const openEditForm = (expense) => {
    setExpenseForm({
      name: expense.name || "",
      amount: expense.amount || "",
      date: expense.date ? expense.date.slice(0, 10) : "",
      category: expense.category?._id || "",
      description: expense.description || "",
      // Older rows predate the flag: no organizationId means it was ours.
      isSystem: expense.isSystem ?? !expense.organizationId,
      organizationId:
        expense.organizationId?._id || expense.organizationId || "",
    });
    setEditingExpense(expense);
    setIsExpenseFormOpen(true);
  };
  const handleExpenseSubmit = (e) => {
    e.preventDefault();
    if (!expenseForm.category) {
      showToast("error", "Please select a category");
      return;
    }
    const payload = {
      ...expenseForm,
      amount: parseFloat(expenseForm.amount),
      isSystem: expenseForm.isSystem,
      // A Beleef expense is our own cost — there is no org to attribute it to.
      organizationId: expenseForm.isSystem
        ? undefined
        : expenseForm.organizationId || undefined,
    };
    if (editingExpense) {
      updateExpense(
        { id: editingExpense._id, ...payload },
        {
          onSuccess: () => setIsExpenseFormOpen(false),
        },
      );
    } else {
      createExpense(payload, {
        onSuccess: () => setIsExpenseFormOpen(false),
      });
    }
  };
  const handleExpenseDelete = () => {
    if (deleteConfirmId) {
      deleteExpense(deleteConfirmId, {
        onSuccess: () => setDeleteConfirmId(null),
      });
    }
  };

  const renderExpenseItems = useCallback(
    (node) => (
      <div className="divide-y divide-lightgray">
        {node._items.map((exp) => (
          <div
            key={exp._id}
            className="flex items-start justify-between px-6 py-2.5 bg-lightergray"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-darkergray">{exp.name}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                <span className="text-xs text-darkgray">
                  {exp.date
                    ? new Date(exp.date).toLocaleDateString("en-AU")
                    : "—"}
                </span>
                {exp.organizationId?.name && (
                  <span className="text-xs text-darkgray">
                    · {exp.organizationId.name}
                  </span>
                )}
                {exp.description && (
                  <span className="text-xs text-darkgray truncate max-w-xs">
                    · {exp.description}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 ml-4 shrink-0">
              <span className="text-sm font-semibold text-red-500 tabular-nums">
                {formatCurrency(exp.amount)}
              </span>
              <button type="button" onClick={() => openEditForm(exp)}>
                <Pencil className="edit-icon" />
              </button>
              <button type="button" onClick={() => setDeleteConfirmId(exp._id)}>
                <Trash2 className="delete-icon" />
              </button>
            </div>
          </div>
        ))}
      </div>
    ),
    [openEditForm, setDeleteConfirmId],
  );

  // Helper: look up a platform's summary data by key.
  const platformFor = useCallback(
    (k) => (platformSummary?.platforms ?? []).find((p) => p.key === k),
    [platformSummary],
  );

  /*  Derived: Revenue rows  */
  const revenueRows = useMemo(() => {
    const subscriptionCount = cards.subscription?.count ?? 0;
    const campaignFeeCount = cards.campaignFee?.count ?? 0;
    // Keep the decimal — Math.round would show the 4.4% rate as "4%"
    const serviceRate = parseFloat(
      ((cards.service?.rate || 0) * 100).toFixed(1),
    );
    const serviceTotalAmount = cards.service?.totalServices ?? 0;
    const otherCount = cards.other?.count ?? 0;

    return [
      {
        type: "Subscriptions",
        subtitle: `${subscriptionCount} active subscription${subscriptionCount !== 1 ? "s" : ""}`,
        amount: cards.subscription?.total ?? totals.subscriptionTotal ?? 0,
      },
      {
        type: "Campaign Fees",
        subtitle: `${campaignFeeCount} campaign fee${campaignFeeCount !== 1 ? "s" : ""}`,
        amount: cards.campaignFee?.total ?? totals.campaignFeeTotal ?? 0,
      },
      {
        type: "Service Revenue",
        subtitle: serviceRate
          ? `${serviceRate}% on ${formatCurrency(serviceTotalAmount)} total services`
          : "commission",
        amount: cards.service?.total ?? totals.beleefTotal ?? 0,
      },
      {
        type: "Other Revenue",
        subtitle: `${otherCount} purchase${otherCount !== 1 ? "s" : ""}`,
        amount: cards.other?.total ?? totals.otherTotal ?? 0,
      },
    ];
  }, [cards, totals]);

  // Sum all revenue rows (overallTotal from the backend intentionally excludes
  // Other Revenue for the detailed Financials page — here we want the full total).
  const totalRevenue = revenueRows.reduce((sum, r) => sum + r.amount, 0);

  /*  Derived: Connected cost rows — built from the single summary endpoint.  */
  const connectedCostRows = useMemo(() => {
    const platforms = platformSummary?.platforms ?? [];
    const byKey = Object.fromEntries(platforms.map((p) => [p.key, p]));

    const usageDesc = (k) => {
      const u = byKey[k]?.usage;
      if (!u) return PLATFORMS.find((p) => p.key === k)?.description ?? "";
      if (AI_PROVIDER_KEYS.includes(k)) {
        return `${(u.totalCalls ?? 0).toLocaleString()} calls · ${(u.totalTokens ?? 0).toLocaleString()} tokens`;
      }
      if (k === "twilio" || k === "intellisms") {
        return `SMS — ${(u.totalMessages ?? 0).toLocaleString()} messages`;
      }
      if (k === "domain") {
        return `Property data — ${(u.totalBillableCalls ?? 0).toLocaleString()} billable calls`;
      }
      if (k === "ayrshare") {
        return `Social posting — ${(u.totalPosts ?? 0).toLocaleString()} posts`;
      }
      if (k === "dyedurham") {
        return `Land title — ${(u.totalSearches ?? 0).toLocaleString()} searches`;
      }
      if (k === "globaldata") {
        return `ASIC — ${(u.totalSearches ?? 0).toLocaleString()} searches`;
      }
      return "";
    };

    return PLATFORMS.map((p) => ({
      key: p.key,
      label: p.label,
      description: usageDesc(p.key),
      amount: byKey[p.key]?.monthlyCost ?? 0,
      hasError: isCostError,
    }));
  }, [platformSummary, isCostError]);

  /*  Derived: Summary numbers ─ */
  const moneyIn = totalRevenue;
  const moneyOut =
    connectedCostRows
      .filter((r) => activePlatforms[r.key])
      .reduce((sum, r) => sum + r.amount, 0) + expenseTotal;
  const profit = moneyIn - moneyOut;
  const margin = moneyIn > 0 ? Math.round((profit / moneyIn) * 100) : 0;

  /*  Table column definitions ─ */
  const revenueColumns = useMemo(
    () => [
      {
        accessorKey: "type",
        header: "Revenue Type",
        isSortable: false,
        cell: (row) => (
          <div>
            <div className="text-sm font-medium text-darkergray">
              {row.type}
            </div>
            <div className="text-xs text-darkgray mt-0.5">{row.subtitle}</div>
          </div>
        ),
      },
      {
        accessorKey: "amount",
        header: "Amount",
        isSortable: false,
        cell: (row) => (
          <span className="text-sm font-semibold text-green-600 tabular-nums">
            {formatCurrency(row.amount)}
          </span>
        ),
      },
      {
        accessorKey: "share",
        header: "Share",
        isSortable: false,
        isFilter: false,
        exportValue: (row) =>
          totalRevenue > 0
            ? `${Math.round((row.amount / totalRevenue) * 100)}%`
            : "0%",
        cell: (row) => {
          const share =
            totalRevenue > 0
              ? Math.round((row.amount / totalRevenue) * 100)
              : 0;
          return (
            <div className="flex items-center justify-end gap-2">
              <div className="flex-1 max-w-24 bg-lightgray rounded-full h-1.5">
                <div
                  className="bg-green-500 h-1.5 rounded-full"
                  style={{ width: `${share}%` }}
                />
              </div>
              <span className="text-xs text-darkgray w-8 text-right tabular-nums">
                {share}%
              </span>
            </div>
          );
        },
      },
    ],
    [totalRevenue],
  );

  // Whether a provider recorded any usage in the selected range.
  const rowHasUsage = useCallback(
    (row) => {
      if (!row) return false;
      const u = platformFor(row.key)?.usage ?? {};
      if (AI_PROVIDER_KEYS.includes(row.key)) return (u.totalCalls ?? 0) > 0;
      if (row.key === "twilio" || row.key === "intellisms") return (u.totalMessages ?? 0) > 0;
      if (row.key === "domain") return (u.totalCalls ?? 0) > 0;
      if (row.key === "ayrshare") return (u.totalPosts ?? 0) > 0;
      if (row.key === "dyedurham" || row.key === "globaldata") return (u.totalSearches ?? 0) > 0;
      return false;
    },
    [platformFor],
  );

  // Every loaded provider row opens its drill-down modal, even with no usage,
  // because the modal is where the monthly cost is set.
  const rowIsDrillable = useCallback(
    (row) => !!row && !!platformFor(row.key),
    [platformFor],
  );

  const costColumns = useMemo(
    () => [
      {
        accessorKey: "label",
        header: "Cost",
        isSortable: false,
        cell: (row) => {
          const platform = PLATFORMS.find((p) => p.key === row.key);
          const isActive = activePlatforms[row.key];
          return (
            <div className="flex items-center gap-2">
              <span
                className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${platform?.dotColor ?? "bg-gray-400"}`}
              />
              <span
                className={`text-sm font-medium ${isActive ? "text-darkergray" : "text-darkgray"}`}
              >
                {row.label}
              </span>
              {/* AI providers open a modal rather than expanding in place. */}
              {rowIsDrillable(row) ? (
                <span className="text-xs text-darkgray underline decoration-mediumgray underline-offset-2">
                  {rowHasUsage(row) ? "view usage" : "set cost"}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: "description",
        header: "What It's For",
        isSortable: false,
        cell: (row) => (
          <span className="text-sm text-darkgray">{row.description}</span>
        ),
      },
      {
        accessorKey: "amount",
        header: "Cost",
        isSortable: false,
        cell: (row) => {
          if (row.hasError)
            return (
              <span className="text-xs text-red-400 italic">
                Failed to load
              </span>
            );
          if (!activePlatforms[row.key])
            return <span className="text-sm text-darkgray">—</span>;
          if (row.amount === 0)
            return <span className="text-sm text-darkgray italic">not set</span>;
          return (
            <span className="text-sm font-semibold text-red-500 tabular-nums">
              {formatCurrency(row.amount)}
            </span>
          );
        },
      },
    ],
    [activePlatforms, rowIsDrillable, rowHasUsage],
  );

  // One row per expense category for the second cost table
  const expenseCategoryColumns = useMemo(
    () => [
      {
        accessorKey: "label",
        header: "Category",
        isSortable: false,
        cell: (row) => (
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 bg-purple-500" />
            <span className="text-sm font-medium text-darkergray">
              {row.label}
            </span>
            <span className="text-xs text-darkgray underline decoration-mediumgray underline-offset-2">
              view expenses
            </span>
          </div>
        ),
      },
      {
        accessorKey: "product",
        header: "Product",
        isSortable: false,
        cell: (row) => {
          if (!row._categoryId) return null;
          const saved = categoryTagFor(row._categoryId);
          // A split is edited in Manage Categories; picking one product here
          // would quietly throw it away.
          if (saved?.splits?.length) {
            return (
              <span className="text-xs text-darkergray">
                {describeSplit(saved)}
              </span>
            );
          }
          return (
            <CategoryProductInput
              key={row._categoryId}
              defaultValue={saved?.product ?? ""}
              onSave={(product) =>
                saveProduct({
                  id: saved?._id,
                  keyType: "expenseCategory",
                  key: row._categoryId,
                  product,
                })
              }
            />
          );
        },
      },
      {
        accessorKey: "count",
        header: "Items",
        isSortable: false,
        cell: (row) => (
          <span className="text-sm text-darkgray">
            {row._items.length} entr{row._items.length === 1 ? "y" : "ies"}
          </span>
        ),
      },
      {
        accessorKey: "total",
        header: "Total",
        isSortable: false,
        cell: (row) => (
          <span className="text-sm font-semibold text-red-500 tabular-nums">
            {formatCurrency(row.total)}
          </span>
        ),
      },
    ],
    [categoryTagFor, saveProduct],
  );

  const subtitle = "Money In and Money Out — simple view";

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
        <div>
          <h1 className="tracking-widest uppercase text-darkergray">
            Financial Summary
          </h1>
          <p className="text-sm text-darkgray mt-0.5">{subtitle}</p>
        </div>

        <div className="w-56 flex-shrink-0">
          <DatePicker value={dateValue} onChange={setDateValue} />
        </div>
      </div>

      {/* filter — single point of control: drives Money Out, cost table, organizations, and agent drill-down alike */}
      <div className="flex items-center gap-2.5 mb-6 flex-wrap">
        <div className="flex items-center gap-1.5 text-sm text-darkgray flex-shrink-0">
          <Filter className="w-3.5 h-3.5" />
          <span>Platforms:</span>
        </div>
        {PLATFORMS.map((p) => (
          <button
            key={p.key}
            onClick={() => togglePlatform(p.key)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${
              activePlatforms[p.key]
                ? `bg-white border-mediumgray ${p.textColor}`
                : "bg-lightergray border-lightergray text-darkgray opacity-40"
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full flex-shrink-0 ${p.dotColor}`}
            />
            {p.label}
          </button>
        ))}
        <button
          onClick={resetPlatforms}
          className="ml-auto text-xs text-darkgray hover:text-darkergray underline"
        >
          Reset
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {/* Money In */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-5">
          <div className="flex items-center gap-1.5 mb-3">
            <ArrowDownCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-xs font-semibold tracking-widest uppercase text-green-700">
              Money In
            </span>
          </div>
          {isFinancialLoading ? (
            <Loader2 className="w-6 h-6 animate-spin text-green-400 mb-1.5" />
          ) : isFinancialError ? (
            <div className="text-sm text-red-400 italic mb-1.5">
              Unavailable
            </div>
          ) : (
            <div className="text-3xl font-bold text-green-600 mb-1.5 tabular-nums">
              {formatCurrency(moneyIn)}
            </div>
          )}
          <div className="text-xs text-darkgray">Revenue from customers</div>
        </div>

        {/* Money Out */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="flex items-center gap-1.5 mb-3">
            <ArrowUpCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-xs font-semibold tracking-widest uppercase text-red-600">
              Money Out
            </span>
          </div>
          {isCostLoading ? (
            <Loader2 className="w-6 h-6 animate-spin text-red-300 mb-1.5" />
          ) : (
            <div className="text-3xl font-bold text-red-500 mb-1.5 tabular-nums">
              {formatCurrency(moneyOut)}
            </div>
          )}
          <div className="text-xs text-darkgray">
            API costs this period (USD)
          </div>
        </div>

        {/* Profit / Loss */}
        <div className={`border rounded-xl p-5 ${profit < 0 ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
          <div className="flex items-center gap-1.5 mb-3">
            <TrendingUp className={`w-4 h-4 flex-shrink-0 ${profit < 0 ? "text-red-600" : "text-emerald-700"}`} />
            <span className={`text-xs font-semibold tracking-widest uppercase ${profit < 0 ? "text-red-600" : "text-emerald-700"}`}>
              {profit < 0 ? "Loss" : "Profit"}
            </span>
          </div>
          {isFinancialLoading || isCostLoading ? (
            <Loader2 className={`w-6 h-6 animate-spin mb-1.5 ${profit < 0 ? "text-red-400" : "text-emerald-400"}`} />
          ) : isFinancialError ? (
            <div className="text-sm text-red-400 italic mb-1.5">
              Unavailable
            </div>
          ) : (
            <div className={`text-3xl font-bold mb-1.5 tabular-nums ${profit < 0 ? "text-red-600" : "text-emerald-700"}`}>
              {formatCurrency(profit)}
            </div>
          )}
          <div className="text-xs text-darkgray">
            {margin}% margin · Money In minus Money Out
          </div>
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <ArrowDownCircle className="w-4 h-4 text-green-600" />
          <h2 className="text-xs font-bold tracking-widest uppercase text-green-700">
            Where does the money come from?
          </h2>
        </div>

        {isFinancialLoading ? (
          <div className="bg-white border border-mediumgray rounded-lg overflow-hidden py-10 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-darkgray" />
          </div>
        ) : isFinancialError ? (
          <div className="bg-white border border-mediumgray rounded-lg px-5 py-8 text-center text-sm text-darkgray">
            Revenue data could not be loaded. Please try refreshing.
          </div>
        ) : (
          <>
            <Table
              columns={revenueColumns}
              data={revenueRows}
              searchable={false}
              emptyText="No revenue data"
            />
            <div className="flex items-center justify-between px-1 mt-3 pt-3 border-t border-mediumgray">
              <span className="text-sm font-bold text-darkergray">
                Total Revenue
              </span>
              <span className="text-sm font-bold text-green-600 tabular-nums">
                {formatCurrency(totalRevenue)}
              </span>
            </div>
          </>
        )}
      </div>

      {/*  Cost table  */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ArrowUpCircle className="w-4 h-4 text-red-500" />
            <h2 className="tracking-widest uppercase text-red-600">
              Where does the money go?
            </h2>
          </div>
          <button
            onClick={() => {
              openCreateForm();
              setIsExpenseModalOpen(true);
            }}
            className="flex items-center gap-1 text-xs text-darkgray hover:text-darkergray"
          >
            <Plus className="w-3.5 h-3.5" /> Add Expense
          </button>
        </div>

        {isCostLoading ? (
          <div className="bg-white border border-mediumgray rounded-lg overflow-hidden py-10 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-darkgray" />
          </div>
        ) : (
          <>
            {/* Usage costs — API providers */}
            <Table
              columns={costColumns}
              data={connectedCostRows}
              searchable={false}
              emptyText="No cost data"
              onRowClick={(row) => {
                if (rowIsDrillable(row)) setDrillRow(row);
              }}
            />

            {/* Custom expenses — one row per category */}
            {categoryNodes.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-darkgray mb-2 px-1">
                  Custom Expenses
                </p>
                <Table
                  columns={expenseCategoryColumns}
                  data={categoryNodes}
                  searchable={false}
                  emptyText="No expenses"
                  onRowClick={(row) => setDrillCategory(row)}
                />
              </div>
            )}

            <div className="flex items-center justify-between px-1 mt-3 pt-3 border-t border-mediumgray">
              <span className="text-sm font-bold text-darkergray">
                Total Costs
              </span>
              <span className="text-sm font-bold text-red-500 tabular-nums">
                {formatCurrency(moneyOut)}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ── Provider drill-down modals — data lazy-loaded on open ────── */}
      <AiCostBreakdown
        isOpen={!!drillRow && AI_PROVIDER_KEYS.includes(drillRow.key)}
        onClose={() => setDrillRow(null)}
        provider={drillRow?.key}
        label={drillRow?.label ?? ""}
        aiParams={aiParams}
        monthlyPrices={platformFor(drillRow?.key)?.monthlyPrices ?? {}}
        dateValue={dateValue}
        onSetMonthlyPrice={setMonthlyPrice}
        onUpsertProduct={saveProduct}
      />
      <TwilioCostBreakdown
        isOpen={drillRow?.key === "twilio"}
        onClose={() => setDrillRow(null)}
        dateParams={dateParams}
        monthlyPrices={platformFor("twilio")?.monthlyPrices ?? {}}
        dateValue={dateValue}
        onSetMonthlyPrice={setMonthlyPrice}
        onUpsertProduct={saveProduct}
      />
      <TwilioCostBreakdown
        isOpen={drillRow?.key === "intellisms"}
        onClose={() => setDrillRow(null)}
        title="IntelliSMS — SMS Usage"
        provider="intellisms"
        dateParams={dateParams}
        monthlyPrices={platformFor("intellisms")?.monthlyPrices ?? {}}
        dateValue={dateValue}
        onSetMonthlyPrice={setMonthlyPrice}
        onUpsertProduct={saveProduct}
      />
      <DomainCostBreakdown
        isOpen={drillRow?.key === "domain"}
        onClose={() => setDrillRow(null)}
        rangeParams={rangeParams}
        monthlyPrices={platformFor("domain")?.monthlyPrices ?? {}}
        dateValue={dateValue}
        onSetMonthlyPrice={setMonthlyPrice}
        onUpsertProduct={saveProduct}
      />
      <AyrshareCostBreakdown
        isOpen={drillRow?.key === "ayrshare"}
        onClose={() => setDrillRow(null)}
        rangeParams={rangeParams}
        monthlyPrices={platformFor("ayrshare")?.monthlyPrices ?? {}}
        dateValue={dateValue}
        onSetMonthlyPrice={setMonthlyPrice}
        onUpsertProduct={saveProduct}
      />
      <RegistrySearchBreakdown
        isOpen={drillRow?.key === "dyedurham"}
        onClose={() => setDrillRow(null)}
        provider="dyedurham"
        title="Dye & Durham — Land Title Searches"
        rangeParams={rangeParams}
        monthlyPrices={platformFor("dyedurham")?.monthlyPrices ?? {}}
        dateValue={dateValue}
        onSetMonthlyPrice={setMonthlyPrice}
      />
      <RegistrySearchBreakdown
        isOpen={drillRow?.key === "globaldata"}
        onClose={() => setDrillRow(null)}
        provider="globaldata"
        title="GlobalData — ASIC Searches"
        rangeParams={rangeParams}
        monthlyPrices={platformFor("globaldata")?.monthlyPrices ?? {}}
        dateValue={dateValue}
        onSetMonthlyPrice={setMonthlyPrice}
      />

      {/* ── Category expense drill-down modal ──────────────────────────── */}
      <CostBreakdownModal
        isOpen={!!drillCategory}
        onClose={() => setDrillCategory(null)}
        title={drillCategory?.label ?? ""}
        subtitle={`${drillCategory?._items?.length ?? 0} expense${(drillCategory?._items?.length ?? 0) !== 1 ? "s" : ""} · ${formatCurrency(drillCategory?.total ?? 0)}`}
        levels={[
          {
            header: "Expense",
            columns: [{ key: "amount", header: "Amount", align: "right" }],
          },
        ]}
        nodes={(drillCategory?._items ?? []).map((exp) => ({
          id: exp._id,
          label: exp.name,
          values: { amount: formatCurrency(exp.amount) },
          sublabel: [
            exp.date ? new Date(exp.date).toLocaleDateString("en-AU") : null,
            exp.organizationId?.name ? `· ${exp.organizationId.name}` : null,
            exp.description ? `· ${exp.description}` : null,
          ]
            .filter(Boolean)
            .join(" "),
          _expense: exp,
        }))}
        nodeActions={(node) => (
          <>
            <button
              type="button"
              onClick={() => {
                setDrillCategory(null);
                openEditForm(node._expense);
              }}
              title="Edit expense"
            >
              <Pencil className="edit-icon" />
            </button>
            <button
              type="button"
              onClick={() => setDeleteConfirmId(node._expense._id)}
              title="Delete expense"
            >
              <Trash2 className="delete-icon" />
            </button>
          </>
        )}
        searchable
        searchPlaceholder="Search expenses…"
        emptyText="No expenses in this category."
      />

      {/* ── Expenses CRUD Modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={isExpenseModalOpen}
        onClose={() => {
          // The category modal is layered on top — its mousedown events land
          // outside this modal's ref and would otherwise close it.
          if (isCategoryModalOpen || deleteConfirmId) return;
          setIsExpenseModalOpen(false);
          setIsExpenseFormOpen(false);
          setEditingExpense(null);
          setDeleteConfirmId(null);
        }}
        title="Expenses"
        className="max-w-4xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-darkgray">
            {expenseCount} expense{expenseCount !== 1 ? "s" : ""} · Total:{" "}
            <span className="font-semibold text-darkergray">
              {formatCurrency(expenseTotal)}
            </span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => {
                setCategoryInput("");
                setEditingCategory(null);
                setDeleteCategoryId(null);
                setIsCategoryModalOpen(true);
              }}
              className="gray-button flex gap-2 items-center"
            >
              Manage Categories
            </Button>
            <Button
              onClick={openCreateForm}
              className="black-button flex gap-2 items-center"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </Button>
          </div>
        </div>

        {/* Add/Edit form */}
        {isExpenseFormOpen && (
          <form
            onSubmit={handleExpenseSubmit}
            className="border border-mediumgray rounded-lg p-4 mb-4 bg-lightergray"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-darkergray mb-1">
                  Name *
                </label>
                <input
                  value={expenseForm.name}
                  onChange={(e) =>
                    setExpenseForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                  className="form-input border border-mediumgray"
                  placeholder="e.g. AWS Hosting"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-darkergray mb-1">
                  Amount (AUD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={expenseForm.amount}
                  onChange={(e) =>
                    setExpenseForm((f) => ({ ...f, amount: e.target.value }))
                  }
                  required
                  className="form-input border border-mediumgray"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-darkergray mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={expenseForm.date}
                  onChange={(e) =>
                    setExpenseForm((f) => ({ ...f, date: e.target.value }))
                  }
                  required
                  className="form-input border border-mediumgray"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-darkergray mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <SearchSelect
                  options={categoryOptions}
                  value={
                    categoryOptions.find(
                      (o) => o.value === expenseForm.category,
                    ) || null
                  }
                  onChange={(opt) =>
                    setExpenseForm((f) => ({
                      ...f,
                      category: opt?.value || "",
                    }))
                  }
                  placeholder="Select category"
                  isClearable
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-darkergray mb-1">
                  Expense for
                </label>
                <div className="flex items-center gap-0.5 border border-mediumgray bg-white rounded p-0.5">
                  {[
                    { value: true, label: "Beleef" },
                    { value: false, label: "An organization" },
                  ].map((opt) => (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() =>
                        setExpenseForm((f) => ({
                          ...f,
                          isSystem: opt.value,
                          // Switching back to a Beleef expense clears the
                          // customer field so it can't be submitted.
                          ...(opt.value ? { organizationId: "" } : {}),
                        }))
                      }
                      className={`flex-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                        expenseForm.isSystem === opt.value
                          ? "bg-darkergray text-white"
                          : "text-darkgray hover:bg-lightergray"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {!expenseForm.isSystem ? (
                <div>
                  <label className="block text-xs font-medium text-darkergray mb-1">
                    Organization <span className="text-red-500">*</span>
                  </label>
                  <SearchSelect
                    options={orgOptions}
                    value={
                      orgOptions.find(
                        (o) => o.value === expenseForm.organizationId,
                      ) || null
                    }
                    onChange={(opt) =>
                      setExpenseForm((f) => ({
                        ...f,
                        organizationId: opt?.value || "",
                      }))
                    }
                    placeholder="Select an organization"
                    isClearable
                  />
                </div>
              ) : null}
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-darkergray mb-1">
                Description
              </label>
              <textarea
                value={expenseForm.description}
                onChange={(e) =>
                  setExpenseForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={2}
                className="form-input border border-mediumgray"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                loading={isCreating || isUpdating}
                className="black-button flex gap-2 items-center"
              >
                {editingExpense ? "Update" : "Add"} Expense
              </Button>
              <Button
                onClick={() => {
                  setIsExpenseFormOpen(false);
                  setEditingExpense(null);
                }}
                className="gray-button flex gap-2 items-center"
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Expenses by category */}
        {isExpensesLoading ? (
          <CostBreakdownLoading text="Loading expenses…" />
        ) : categoryNodes.length === 0 ? (
          <CostBreakdownEmpty text="No custom expenses for this period" />
        ) : (
          <div className="border border-lightgray rounded-lg overflow-hidden">
            <CostBreakdownSubtree
              nodes={categoryNodes}
              depth={0}
              levels={EXPENSE_TREE_LEVELS}
              renderLazy={renderExpenseItems}
            />
          </div>
        )}
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Expense"
      >
        <p className="text-sm text-darkgray mb-4">
          Are you sure you want to delete this expense? This cannot be undone.
        </p>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleExpenseDelete}
            loading={isDeleting}
            className="flex gap-2 items-center px-4 py-2 rounded text-sm bg-red-500 text-white hover:opacity-80"
          >
            Delete
          </Button>
          <Button
            onClick={() => setDeleteConfirmId(null)}
            className="gray-button flex gap-2 items-center"
          >
            Cancel
          </Button>
        </div>
      </Modal>

      {/* ── Manage Categories Modal ─────────────────────────────────────── */}
      <Modal
        isOpen={isCategoryModalOpen}
        onClose={() => {
          setIsCategoryModalOpen(false);
          setEditingCategory(null);
          setCategoryInput("");
          setDeleteCategoryId(null);
          setSplitCategoryId(null);
        }}
        title="Manage Categories"
      >
        {/* Add / Edit form */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const name = categoryInput.trim();
            if (!name) return;
            if (editingCategory) {
              updateCategory(
                { id: editingCategory._id, name },
                {
                  onSuccess: () => {
                    setEditingCategory(null);
                    setCategoryInput("");
                  },
                },
              );
            } else {
              createCategory(
                { name },
                {
                  onSuccess: () => setCategoryInput(""),
                },
              );
            }
          }}
          className="flex items-center gap-2 mb-4"
        >
          <input
            value={categoryInput}
            onChange={(e) => setCategoryInput(e.target.value)}
            placeholder={
              editingCategory ? "Rename category" : "New category name"
            }
            required
            className="form-input flex-1 border border-mediumgray"
          />
          <Button
            type="submit"
            loading={isCreatingCategory || isUpdatingCategory}
            className="black-button flex gap-2 items-center whitespace-nowrap"
          >
            {editingCategory ? "Save" : "Add"}
          </Button>
          {editingCategory && (
            <Button
              onClick={() => {
                setEditingCategory(null);
                setCategoryInput("");
              }}
              className="gray-button flex gap-2 items-center"
            >
              Cancel
            </Button>
          )}
        </form>

        {/* Category list */}
        <div className="mt-3 border border-mediumgray rounded-lg overflow-hidden">
          {(expenseCategories || []).length === 0 ? (
            <p className="px-4 py-3 text-sm text-darkgray">
              No categories yet. Add one above.
            </p>
          ) : (
            (expenseCategories || []).map((cat, idx) => {
              const tag = categoryTagFor(cat._id);
              const isSplitOpen = splitCategoryId === cat._id;
              return (
                <div
                  key={cat._id}
                  className={idx !== 0 ? "border-t border-mediumgray" : ""}
                >
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <div className="min-w-0">
                      <span className="text-sm text-darkergray">{cat.name}</span>
                      <p className="text-xs text-darkgray truncate">
                        {describeSplit(tag) || "No product set"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        type="button"
                        onClick={() =>
                          setSplitCategoryId(isSplitOpen ? null : cat._id)
                        }
                        className="text-xs text-darkgray underline decoration-mediumgray underline-offset-2"
                      >
                        {isSplitOpen ? "Close" : "Product split"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingCategory(cat);
                          setCategoryInput(cat.name);
                        }}
                      >
                        <Pencil className="edit-icon" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteCategoryId(cat._id)}
                      >
                        <Trash2 className="delete-icon" />
                      </button>
                    </div>
                  </div>
                  {isSplitOpen && (
                    <ProductSplitEditor
                      key={`${cat._id}-${describeSplit(tag)}`}
                      tag={tag}
                      isSaving={isCreatingProduct || isUpdatingProduct}
                      onSave={(splits) =>
                        saveProduct(
                          {
                            id: tag?._id,
                            keyType: "expenseCategory",
                            key: cat._id,
                            splits,
                          },
                          {
                            onSuccess: () => {
                              setSplitCategoryId(null);
                              showToast(
                                "success",
                                `Product split saved for ${cat.name}`,
                              );
                            },
                          },
                        )
                      }
                    />
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Delete category confirmation (inline) */}
        {deleteCategoryId && (
          <div className="mt-4 p-3 border border-red-200 bg-red-50 rounded-lg">
            <p className="text-sm text-darkgray mb-3">
              Delete this category? Expenses using it will become Uncategorized.
            </p>
            <div className="flex items-center gap-2">
              <Button
                onClick={() =>
                  deleteCategory(deleteCategoryId, {
                    onSuccess: () => setDeleteCategoryId(null),
                  })
                }
                loading={isDeletingCategory}
                className="flex gap-2 items-center px-4 py-2 rounded text-sm bg-red-500 text-white hover:opacity-80"
              >
                Delete
              </Button>
              <Button
                onClick={() => setDeleteCategoryId(null)}
                className="gray-button flex gap-2 items-center"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FinancialSummary;
